import { api } from "../../api";

const createQueryParams = (params, isAdditionalFilters) => {
    if (!params || typeof params !== "object") return "";

    const appliedFilters = Object.keys(params)
        .map((key) => {
            const value = params[key];
            if (value === undefined || value === null || value === "")
                return "";
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .filter(Boolean)
        .join("&");

    if (!appliedFilters) return "";
    return isAdditionalFilters ? `?${appliedFilters}` : `&${appliedFilters}`;
};

export const dashboardApi = api.injectEndpoints({
    endpoints: (build) => ({
        getPublicationCalendar: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const searchParam = params.searchFields?.analyst_name
                    ? `&search=${params.searchFields.analyst_name}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/publication-calendar/?include_bespoke=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${searchParam}${appliedFilters}`;
            },
        }),
        getPublicationHeatMap: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/publication-heatmap/?start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getPublicationCalendarHistory: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/publication-calendar/?start_date=${params.startDate}&end_date=${params.endDate}&history=true${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getTargetMissed: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/target-missed/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getWeeklySummary: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/weekly-summary/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getTrackInitiations: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/track-initiations/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getCoverageSpreads: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/coverage-performance/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getPerformanceSectors: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/performance-sector/?start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLongNotesAnalysis: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/long-notes-analysis/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getCorporatePlansSummary: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/corporate-plans-summary/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getTrendView: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);

                return `/research/trend/?start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getHistoricalView: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);

                return `/research/historical/?start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getTop25: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/top-25/?start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getUpcomingPublicationSchedule: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/upcoming-publication-schedule/?start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getCoverageUniverse: build.query({
            query: () => {
                return `/research/coverage-universe/`;
            },
        }),
        getRatingSpread: build.query({
            query: () => {
                return `/research/rating-spread/`;
            },
        }),
        getAnalystList: build.query({
            query: () => `/research/analysts/`,
            providesTags: ["AnalystList"],
        }),
        getAnalystDetail: build.query({
            query: (params) => {
                return `/research/analysts/${params.id}/?start_date=${params.startDate}&end_date=${params.endDate}`;
            },
        }),
        getSectorPublications: build.query({
            query: () => {
                return `/research/sector-publications/`;
            },
        }),
        getDynamicFilterOptions: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params, true);
                return `/research/dynamic-filter-options/${appliedFilters}`;
            },
        }),
        getPublicationsExtract: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/publication-reports/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getCorporateEngagementsCalendar: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/corporate-engagements-calendar/?start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}${pageParams}${pageSizeParams}`;
            },
        }),
        getAnalystsVotesSpread: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/analyst-votes-spread/?start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getCorporateTouchPoints: build.query({
            query: (params) => {
                return `/research/corporate-touch-points/?start_date=${params.startDate}&end_date=${params.endDate}`;
            },
        }),
        getSectorsVotesSpread: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/analyst-votes-spread/analyst/${params.appliedFilters.analyst_id}/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getAnalystServiceTouchPoints: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/analyst-touchpoint-summary/analyst/${params.appliedFilters.analyst_id}/?start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getCorporateAccessGroups: build.query({
            query: () => {
                return `/research/corporate-access-groups/`;
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetCorporateTouchPointsQuery,
    useGetPublicationCalendarQuery,
    useGetPublicationCalendarHistoryQuery,
    useGetTargetMissedQuery,
    useGetWeeklySummaryQuery,
    useGetTrackInitiationsQuery,
    useGetCoverageSpreadsQuery,
    useGetPerformanceSectorsQuery,
    useGetLongNotesAnalysisQuery,
    useGetPublicationHeatMapQuery,
    useGetCorporatePlansSummaryQuery,
    useGetUpcomingPublicationScheduleQuery,
    useGetCoverageUniverseQuery,
    useGetRatingSpreadQuery,
    useGetAnalystListQuery,
    useGetAnalystDetailQuery,
    useGetSectorPublicationsQuery,
    useGetDynamicFilterOptionsQuery,
    useGetPublicationsExtractQuery,
    useGetAnalystsVotesSpreadQuery,
    useGetSectorsVotesSpreadQuery,
    useGetCorporateEngagementsCalendarQuery,
    useGetAnalystServiceTouchPointsQuery,
    useGetCorporateAccessGroupsQuery,
    useGetTrendViewQuery,
    useGetHistoricalViewQuery,
    useGetTop25Query,
} = dashboardApi;
